import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Controller, useForm } from 'react-hook-form';
import { BiContainer, Button, ConfirmationModal, PasswordField, TextButton, Typography } from 'seagull';
import { routes } from '~/constants';
import { useEventReporter } from '~/event-reporter';
import { usePasswordStrength } from '~/hooks/usePasswordStrength';
import { useLocaleKeys } from '~/i18n';
import XeroSignInButton from '~/pages/unAuthorizedPages/components/XeroSignInButton/XeroSignInButton';
import { useStore, useStoreActions } from '~/store/store-context';
import { validatePassword } from '~/utils/password-policy';
import GoogleButton from '../GoogleButton/GoogleButton';
import IntuitButton from '../IntuitButton/IntuitButton';
import styles from './SecureLoginModal.module.scss';
const SECURE_LOGIN_SET_FLAG = 'secure-login-set-success';
const SECURE_LOGIN_PENDING_PASSWORD_STEP_FLAG = 'secure-login-pending-password-step';
export const SecureLoginModal = (param)=>{
    let { dismissModal, isDismissDisabled = false } = param;
    const { api, logBI } = useStoreActions();
    const { reportToHeap } = useEventReporter();
    const [step, setStep] = useState(()=>localStorage.getItem(SECURE_LOGIN_PENDING_PASSWORD_STEP_FLAG) === 'true' ? 'setPassword' : 'selectIdentity');
    const form = useForm({
        mode: 'all',
        defaultValues: {
            password: '',
            ['confirm-password']: ''
        }
    });
    const { handleSubmit } = form;
    const setPassword = async (data)=>{
        logBI(18102);
        logBI(18902);
        reportToHeap('secure-login', 'click', 'client-click-set-login-password');
        const { password } = data;
        await api.auth.setPassword(password);
        localStorage.setItem(SECURE_LOGIN_SET_FLAG, 'true');
        localStorage.removeItem(SECURE_LOGIN_PENDING_PASSWORD_STEP_FLAG);
        dismissModal(true);
    };
    return /*#__PURE__*/ _jsx(BiContainer, {
        name: "secure_login",
        children: /*#__PURE__*/ _jsxs("div", {
            "data-testid": "secure-login-modal",
            children: [
                step === 'selectIdentity' && /*#__PURE__*/ _jsx(SelectIdentityTypeStep, {
                    dismissModal: dismissModal,
                    onCreatePassword: ()=>{
                        logBI(18901);
                        localStorage.setItem(SECURE_LOGIN_PENDING_PASSWORD_STEP_FLAG, 'true');
                        setStep('setPassword');
                    },
                    isDismissDisabled: isDismissDisabled
                }),
                step === 'setPassword' && /*#__PURE__*/ _jsx(SetPasswordStep, {
                    dismissModal: dismissModal,
                    onClickBack: ()=>setStep('selectIdentity'),
                    onContinue: handleSubmit(setPassword),
                    form: form,
                    isDismissDisabled: isDismissDisabled
                })
            ]
        })
    });
};
const SelectIdentityTypeStep = (param)=>{
    let { onCreatePassword, dismissModal, isDismissDisabled } = param;
    const { t: texts } = useLocaleKeys('components.secureLoginModal');
    const userInfo = useStore((state)=>state.userInfo);
    const history = useHistory();
    const { logBI } = useStoreActions();
    const ensureEmail = userInfo === null || userInfo === void 0 ? void 0 : userInfo.email();
    return /*#__PURE__*/ _jsx(ConfirmationModal, {
        title: texts.title(),
        content: /*#__PURE__*/ _jsxs("div", {
            className: styles.container,
            children: [
                /*#__PURE__*/ _jsx("div", {
                    className: styles.textContainer,
                    children: /*#__PURE__*/ _jsx(Typography, {
                        children: "Creating a Service Provider account requires a secure authentication method. Choose from the following options:"
                    })
                }),
                /*#__PURE__*/ _jsx("div", {
                    className: styles.formContainer,
                    children: /*#__PURE__*/ _jsxs("div", {
                        className: styles.formComponentsWrapper,
                        children: [
                            /*#__PURE__*/ _jsx(Button, {
                                skin: "darkSecondary",
                                size: "medium",
                                fluid: true,
                                testId: "create-password-btn",
                                bi: {
                                    name: 'create_password'
                                },
                                onClick: onCreatePassword,
                                children: "Create a password"
                            }),
                            /*#__PURE__*/ _jsx(GoogleButton, {
                                ensureEmail: ensureEmail,
                                onAuthSuccess: ()=>{
                                    logBI(18903);
                                    history.push(routes.home);
                                }
                            }),
                            /*#__PURE__*/ _jsx(XeroSignInButton, {
                                mode: "signIn",
                                ensureEmail: ensureEmail
                            }),
                            /*#__PURE__*/ _jsx(IntuitButton, {
                                mode: "signIn",
                                ensureEmail: ensureEmail
                            })
                        ]
                    })
                })
            ]
        }),
        bi: {
            primaryButton: {
                name: ''
            },
            secondaryButton: {
                name: 'cancel'
            },
            closeButton: {
                name: 'close'
            }
        },
        secondaryButtonLabel: !isDismissDisabled ? 'Cancel' : undefined,
        secondaryButtonTestId: "cancel-btn",
        onSecondaryButtonClick: ()=>{
            if (isDismissDisabled) return;
            dismissModal(false);
        },
        cancelButtonDisabled: isDismissDisabled,
        onCloseButtonClick: ()=>{
            if (isDismissDisabled) return;
            dismissModal(false);
        },
        testId: "select-identity-step"
    });
};
const SetPasswordStep = (param)=>{
    let { onClickBack, onContinue, form, dismissModal, isDismissDisabled } = param;
    const { t: texts } = useLocaleKeys('components.secureLoginModal');
    const { t: commonTexts } = useLocaleKeys('common');
    const { control, getValues, watch } = form;
    const userInfo = useStore((state)=>state.userInfo);
    const passwordValue = watch('password', '');
    const userInputs = [
        userInfo === null || userInfo === void 0 ? void 0 : userInfo.email(),
        userInfo === null || userInfo === void 0 ? void 0 : userInfo.firstName(),
        userInfo === null || userInfo === void 0 ? void 0 : userInfo.lastName()
    ].filter(Boolean);
    const strengthScore = usePasswordStrength(passwordValue, userInputs);
    return /*#__PURE__*/ _jsx(ConfirmationModal, {
        title: texts.setPasswordTitle(),
        content: /*#__PURE__*/ _jsxs("div", {
            className: styles.container,
            children: [
                /*#__PURE__*/ _jsx(TextButton, {
                    underline: "none",
                    testId: "back-btn",
                    onClick: onClickBack,
                    children: '< Back'
                }),
                /*#__PURE__*/ _jsx("div", {
                    className: styles.formContainer,
                    children: /*#__PURE__*/ _jsxs("form", {
                        className: styles.formComponentsWrapper,
                        children: [
                            /*#__PURE__*/ _jsx(Controller, {
                                control: control,
                                name: "password",
                                defaultValue: "",
                                rules: {
                                    required: true,
                                    validate: (value)=>validatePassword(value, [
                                            userInfo === null || userInfo === void 0 ? void 0 : userInfo.email(),
                                            userInfo === null || userInfo === void 0 ? void 0 : userInfo.firstName(),
                                            userInfo === null || userInfo === void 0 ? void 0 : userInfo.lastName()
                                        ].filter(Boolean)),
                                    deps: 'confirm-password'
                                },
                                render: (param)=>{
                                    let { field, fieldState } = param;
                                    var _fieldState_error;
                                    return /*#__PURE__*/ _jsx(PasswordField, _object_spread_props(_object_spread({
                                        bi: {
                                            name: 'secure_login_password'
                                        }
                                    }, field), {
                                        error: !!fieldState.error,
                                        helperText: (_fieldState_error = fieldState.error) === null || _fieldState_error === void 0 ? void 0 : _fieldState_error.message,
                                        showStrengthMeter: true,
                                        strengthScore: strengthScore,
                                        testId: "password",
                                        label: texts.passwordLabel()
                                    }));
                                }
                            }),
                            /*#__PURE__*/ _jsx(Controller, {
                                control: control,
                                name: "confirm-password",
                                defaultValue: "",
                                rules: {
                                    required: true,
                                    validate: (value)=>{
                                        const doPasswordsMatch = getValues('password') === value;
                                        return doPasswordsMatch || 'Passwords do not match';
                                    }
                                },
                                render: (param)=>{
                                    let { field, fieldState } = param;
                                    var _fieldState_error;
                                    return /*#__PURE__*/ _jsx(PasswordField, _object_spread_props(_object_spread({
                                        bi: {
                                            name: 'secure_login_confirm_password'
                                        }
                                    }, field), {
                                        error: !!fieldState.error,
                                        helperText: (_fieldState_error = fieldState.error) === null || _fieldState_error === void 0 ? void 0 : _fieldState_error.message,
                                        testId: "confirm-password",
                                        label: texts.reenterPassword()
                                    }));
                                }
                            })
                        ]
                    })
                })
            ]
        }),
        bi: {
            primaryButton: {
                name: 'continue'
            },
            secondaryButton: {
                name: 'cancel'
            },
            closeButton: {
                name: 'close'
            }
        },
        primaryButtonLabel: commonTexts.buttons.continue(),
        primaryButtonTestId: "continue-btn",
        onPrimaryButtonClick: onContinue,
        secondaryButtonLabel: !isDismissDisabled ? 'Cancel' : undefined,
        secondaryButtonTestId: "cancel-btn",
        onSecondaryButtonClick: ()=>{
            if (isDismissDisabled) return;
            dismissModal(false);
        },
        onCloseButtonClick: ()=>{
            if (isDismissDisabled) return;
            dismissModal(false);
        },
        cancelButtonDisabled: isDismissDisabled,
        testId: "set-password-step"
    });
};
